/**
 * Entitlement key catalogue — the client's mirror of the Credit Management
 * service's `features` collection.
 *
 * NOTE: String values MUST equal the service's feature keys exactly (the rows
 * created via `POST /api/v1/features`; the service's README seeds the canonical
 * set). A key that does not exist upstream resolves as `notCatalogued`, which
 * the resolver deliberately reports as ALLOWED — so a typo here does not lock
 * anybody out, it silently never blocks. `assertEntitlementKeyParity()` at the
 * bottom of this file is what turns that silence into a console warning.
 *
 * Two keys are additionally asserted SERVER-SIDE at debit time, because their
 * `service_configs` row names them in `featureKey`: `verifiedEmailAccess`
 * (service type `infyndVerifiedEmail`) and `contactPhoneAccess` (`phoneNumber`).
 * Every other key here is a UI gate only until something upstream asks about it.
 *
 * Centralize the keys in ONE place, never write a key literal at a call site.
 */

// ---------------------------------------------------------------------------
// Boolean features — a capability that is simply on or off for a tier.
// ---------------------------------------------------------------------------

/** Reveal a contact's phone number. Asserted at debit time. */
export const ENT_CONTACT_PHONE_ACCESS = 'contactPhoneAccess' as const;
/** Reveal a contact's verified email address. Asserted at debit time. */
export const ENT_CONTACT_EMAIL_ACCESS = 'verifiedEmailAccess' as const;
/** Download search results, saved contacts or saved companies as CSV. */
export const ENT_EXPORT_CSV = 'exportCsv' as const;
/**
 * The suppression lists (email / name / website) and their imports.
 *
 * NOTE: the service's README still documents a single combined
 * `emailValidationSuppression` key. The LIVE catalogue does not have it — it was
 * split into `emailValidation` and `suppression`, and the combined key survives
 * only as a grant on the three plans, pointing at no feature row. A gate on it
 * therefore resolves `notCatalogued`, which is ALLOWED, and blocks nothing.
 * Verified against the dev service on 2026-09-14.
 */
export const ENT_SUPPRESSION = 'suppression' as const;
/** Email verification. Catalogued and granted on every tier; no UI reads it yet. */
export const ENT_EMAIL_VALIDATION = 'emailValidation' as const;
/** Custom data requests — bespoke datasets delivered by the data team. */
export const ENT_CUSTOM_DATA = 'customData' as const;
/** Manage orders: every customer's orders, updating them, credit-exempt orders. */
export const ENT_CUSTOM_DATA_MANAGE = 'customDataManage' as const;
/** Sales order number on the order form (Infynd-FE: finance only). */
export const ENT_CUSTOM_DATA_SO_NUMBER = 'customDataSoNumber' as const;
/** Set team status to "delivered" (Infynd-FE: delivery team only). */
export const ENT_CUSTOM_DATA_MARK_DELIVERED = 'customDataMarkDelivered' as const;
/** See the customer's delivery email addresses on staff screens. */
export const ENT_CUSTOM_DATA_VIEW_DELIVERY_EMAILS = 'customDataViewDeliveryEmails' as const;
/** Settings -> Usage: the credit ledger, summary cards and consumption charts. */
export const ENT_CREDIT_USAGE_INSIGHTS = 'creditUsageInsights' as const;
/** Saved searches visible to the whole team rather than only their author. */
export const ENT_SHARED_SAVED_SEARCH = 'sharedSavedSearch' as const;
/** Push records to a connected CRM. */
export const ENT_CRM_INTEGRATION = 'crmIntegration' as const;
/** The enrichment module — CSV enrichment and the workspace grid's providers. */
export const ENT_DATA_ENRICHMENT = 'dataEnrichment' as const;
/** The advanced filter set in prospect search. */
export const ENT_ADVANCED_FILTERS = 'advancedFilters' as const;
/** 24/7 customer support. Marketing-facing; no gate in the product. */
export const ENT_PRIORITY_SUPPORT = 'prioritySupport' as const;
/** Go-to-market data. Marketing-facing; no gate in the product yet. */
export const ENT_GO_TO_MARKET_DATA = 'goToMarketData' as const;
/** Advanced technical support. Marketing-facing; no gate in the product. */
export const ENT_ADVANCED_TECHNICAL_SUPPORT = 'advancedTechnicalSupport' as const;
/** A named account manager. Marketing-facing; no gate in the product. */
export const ENT_DEDICATED_ACCOUNT_MANAGER = 'dedicatedAccountManager' as const;
/** Advanced data insights & analytics. Marketing-facing; no gate yet. */
export const ENT_ADVANCED_DATA_INSIGHTS = 'advancedDataInsights' as const;
/** Enterprise intelligence. Off on Pro, granted above it. No UI reads it yet. */
export const ENT_ENTERPRISE_INTELLIGENCE = 'enterpriseIntelligence' as const;
/** Speciality intelligence. Off on Pro, granted above it. No UI reads it yet. */
export const ENT_SPECIALITY_INTELLIGENCE = 'specialityIntelligence' as const;

// ---------------------------------------------------------------------------
// Limit features — a countable allowance with a ceiling.
//
// `limit: null` means UNLIMITED and is a different thing from 0, which is a
// ceiling nobody can be under. See useEntitlementLimit for how that is read.
// ---------------------------------------------------------------------------

/** Team seats. Counted upstream from active `team_members`. */
export const ENT_SEATS = 'seats' as const;
/** How many saved searches an account may hold. CUMULATIVE — pass `used`. */
export const ENT_SAVED_SEARCH = 'savedSearch' as const;
/** Searches per month. Counted upstream from the ledger. */
export const ENT_SEARCH_AND_PREVIEW = 'searchAndPreview' as const;
/** Records per CSV/CRM download. PER-REQUEST — pass `used: 0`. */
export const ENT_EXPORT_RECORDS_PER_REQUEST = 'exportRecordsPerRequest' as const;
/**
 * How many rows one bulk-select action may take, which is what the
 * select-count dropdown offers.
 *
 * NOT in the service's seeded catalogue yet — see the "Bulk select ceiling"
 * section of the Credit-Management README for the row to create. Until that
 * row exists the key resolves `notCatalogued`, so the dropdown falls back to
 * BULK_SELECT_FALLBACK and nothing is capped. That is the intended rollout
 * order: the gate ships inert and the row switches it on.
 */
export const ENT_BULK_SELECT_MAX = 'bulkSelectMax' as const;
/** Pages of results reachable, 10 records each. PER-REQUEST. */
export const ENT_SEARCH_RESULT_PAGES = 'searchResultPages' as const;
/** Records in one save-to-list action. PER-REQUEST. */
export const ENT_SAVE_LIST_RECORDS_PER_LIST = 'saveListRecordsPerList' as const;
/** Records an existing saved list may hold. CUMULATIVE — pass `used`. */
export const ENT_SAVED_LIST_RECORDS_TOTAL = 'savedListRecordsTotal' as const;
/** Contact records processed in one enrichment run. PER-REQUEST. */
export const ENT_CONTACTS_PROCESSED_PER_RUN = 'contactsProcessedPerRun' as const;
/** Rows in one suppression CSV. PER-REQUEST. */
export const ENT_SUPPRESSION_UPLOAD_ROWS = 'suppressionUploadRows' as const;
/** Rows in one enhancement upload. PER-REQUEST. */
export const ENT_ENHANCEMENT_UPLOAD_ROWS = 'enhancementUploadRows' as const;
/** Rows in one email N2B upload. PER-REQUEST. */
export const ENT_EMAIL_N2B_UPLOAD_ROWS = 'emailN2bUploadRows' as const;
/** Rows in one DNC upload. PER-REQUEST. */
export const ENT_DNC_UPLOAD_ROWS = 'dncUploadRows' as const;
/** Rows in one workspace CSV import. PER-REQUEST. */
export const ENT_WORKSPACE_UPLOAD_ROWS = 'workspaceUploadRows' as const;
/** Size of one custom-data file, in MB. PER-REQUEST. */
export const ENT_CUSTOM_DATA_UPLOAD_MB = 'customDataUploadMb' as const;
/** How many addresses a custom-data delivery may go to. */
export const ENT_CUSTOM_DATA_DELIVERY_EMAILS = 'customDataDeliveryEmails' as const;
/**
 * Hours of inactivity before a session expires. A VALUE, not an allowance:
 * read the limit and apply it. Never gate on this one.
 */
export const ENT_SESSION_TIMEOUT_HOURS = 'sessionTimeoutHours' as const;

// ---------------------------------------------------------------------------
// The catalogue itself
// ---------------------------------------------------------------------------

/**
 * What kind of question a key answers, mirroring the service's `FEATURE_KINDS`.
 *
 * The distinction is not cosmetic: a boolean is read with `isFeatureDenied`,
 * while a limit is read with `getLimit`/`checkLimit` and its `allowed` flag
 * answers only "may they consume one more?" — which is false for a soloist's
 * single seat, and correctly so.
 */
export type EntitlementKind = 'boolean' | 'limit';

interface EntitlementMeta {
  kind: EntitlementKind;
  /** Human label, used in the denial modal and the parity warning. */
  label: string;
}

/**
 * Every key the service catalogues, with the metadata the client needs to read
 * it correctly. Keys are listed here even when nothing in the product gates on
 * them yet (`prioritySupport` and friends are pricing-page copy), because the
 * parity check compares this map against the live catalogue and a partial map
 * would report the rest as drift.
 */
export const ENTITLEMENT_CATALOGUE = {
  [ENT_CONTACT_PHONE_ACCESS]: { kind: 'boolean', label: 'Contact Phone Access' },
  [ENT_CONTACT_EMAIL_ACCESS]: { kind: 'boolean', label: 'Verified Email Address' },
  [ENT_EXPORT_CSV]: { kind: 'boolean', label: 'Export to CSV' },
  [ENT_SUPPRESSION]: { kind: 'boolean', label: 'Suppression' },
  [ENT_EMAIL_VALIDATION]: { kind: 'boolean', label: 'Email Validation' },
  [ENT_CUSTOM_DATA]: { kind: 'boolean', label: 'Custom Data' },
  [ENT_CUSTOM_DATA_MANAGE]: { kind: 'boolean', label: 'Custom Data: Manage Orders' },
  [ENT_CUSTOM_DATA_SO_NUMBER]: { kind: 'boolean', label: 'Custom Data: SO Number' },
  [ENT_CUSTOM_DATA_MARK_DELIVERED]: { kind: 'boolean', label: 'Custom Data: Mark Delivered' },
  [ENT_CUSTOM_DATA_VIEW_DELIVERY_EMAILS]: { kind: 'boolean', label: 'Custom Data: View Delivery Emails' },
  [ENT_CREDIT_USAGE_INSIGHTS]: { kind: 'boolean', label: 'Credit Usage & Consumption Insights' },
  [ENT_SHARED_SAVED_SEARCH]: { kind: 'boolean', label: 'Shared Saved Search' },
  [ENT_CRM_INTEGRATION]: { kind: 'boolean', label: 'CRM Integration' },
  [ENT_DATA_ENRICHMENT]: { kind: 'boolean', label: 'Data Enrichment' },
  [ENT_ADVANCED_FILTERS]: { kind: 'boolean', label: 'Advanced Filters' },
  [ENT_PRIORITY_SUPPORT]: { kind: 'boolean', label: '24/7 Customer Support' },
  [ENT_GO_TO_MARKET_DATA]: { kind: 'boolean', label: 'Go to Market Data' },
  [ENT_ADVANCED_TECHNICAL_SUPPORT]: { kind: 'boolean', label: 'Advanced Technical Support' },
  [ENT_DEDICATED_ACCOUNT_MANAGER]: { kind: 'boolean', label: 'Dedicated Account Manager' },
  [ENT_ADVANCED_DATA_INSIGHTS]: { kind: 'boolean', label: 'Advanced Data Insights & Analytics' },
  [ENT_ENTERPRISE_INTELLIGENCE]: { kind: 'boolean', label: 'Enterprise Intelligence' },
  [ENT_SPECIALITY_INTELLIGENCE]: { kind: 'boolean', label: 'Speciality Intelligence' },

  [ENT_SEATS]: { kind: 'limit', label: 'Seats Included' },
  [ENT_SAVED_SEARCH]: { kind: 'limit', label: 'Saved Search' },
  [ENT_SEARCH_AND_PREVIEW]: { kind: 'limit', label: 'Search & Data Preview' },
  [ENT_EXPORT_RECORDS_PER_REQUEST]: { kind: 'limit', label: 'Export from Prospect Search' },
  [ENT_BULK_SELECT_MAX]: { kind: 'limit', label: 'Bulk Select Limit' },
  [ENT_SEARCH_RESULT_PAGES]: { kind: 'limit', label: 'Pagination' },
  [ENT_SAVE_LIST_RECORDS_PER_LIST]: { kind: 'limit', label: 'Save List' },
  [ENT_SAVED_LIST_RECORDS_TOTAL]: { kind: 'limit', label: 'Existing Saved Lists' },
  [ENT_CONTACTS_PROCESSED_PER_RUN]: { kind: 'limit', label: 'Number of Contacts Processing' },
  [ENT_SUPPRESSION_UPLOAD_ROWS]: { kind: 'limit', label: 'Suppression list rows' },
  [ENT_ENHANCEMENT_UPLOAD_ROWS]: { kind: 'limit', label: 'Enhancement Upload' },
  [ENT_EMAIL_N2B_UPLOAD_ROWS]: { kind: 'limit', label: 'Email N2B Upload' },
  [ENT_DNC_UPLOAD_ROWS]: { kind: 'limit', label: 'DNC Upload' },
  [ENT_WORKSPACE_UPLOAD_ROWS]: { kind: 'limit', label: 'Workspace Upload' },
  [ENT_CUSTOM_DATA_UPLOAD_MB]: { kind: 'limit', label: 'Custom Data Upload' },
  [ENT_CUSTOM_DATA_DELIVERY_EMAILS]: { kind: 'limit', label: 'Custom Data Delivery' },
  [ENT_SESSION_TIMEOUT_HOURS]: { kind: 'limit', label: 'Session Timeout' },
} as const satisfies Record<string, EntitlementMeta>;

export type EntitlementKey = keyof typeof ENTITLEMENT_CATALOGUE;

/** The label the denial modal shows, falling back to the raw key. */
export function entitlementLabel(featureKey: string): string {
  return ENTITLEMENT_CATALOGUE[featureKey as EntitlementKey]?.label ?? featureKey;
}

// ---------------------------------------------------------------------------
// Operation -> key mapping for the contact-unlock gates
// ---------------------------------------------------------------------------

/**
 * The operations the contact-unlock gate can block. Each maps to the
 * entitlement(s) the credit service will assert on the underlying spend, so
 * the client blocks only where the server would too.
 */
export type ContactUnlockOperation =
  | 'email unlock'
  | 'phone unlock'
  | 'contact unlock';

export const CONTACT_UNLOCK_KEYS: Record<
  ContactUnlockOperation,
  readonly EntitlementKey[]
> = {
  'email unlock': [ENT_CONTACT_EMAIL_ACCESS],
  'phone unlock': [ENT_CONTACT_PHONE_ACCESS],
  'contact unlock': [ENT_CONTACT_EMAIL_ACCESS, ENT_CONTACT_PHONE_ACCESS],
};

// ---------------------------------------------------------------------------
// Parity
// ---------------------------------------------------------------------------

/**
 * Warns when this file and the live catalogue have drifted apart.
 *
 * The failure this exists for is completely silent otherwise: a key the service
 * does not catalogue resolves `notCatalogued`, which is ALLOWED, so a renamed or
 * mistyped key turns a gate into a no-op with no error anywhere. Called once by
 * EntitlementGuardProvider when the bundle first arrives, in development only —
 * a mismatch is a developer's problem, not something to shout about in a
 * customer's console.
 *
 * A key present upstream but missing here is reported too: it means the product
 * grew a gateable thing that nothing on the client reads yet.
 */
export function assertEntitlementKeyParity(serviceKeys: readonly string[]): void {
  if (process.env.NODE_ENV === 'production') return;

  const upstream = new Set(serviceKeys);
  const local = Object.keys(ENTITLEMENT_CATALOGUE);

  const missingUpstream = local.filter((key) => !upstream.has(key));
  const missingLocally = serviceKeys.filter(
    (key) => !(key in ENTITLEMENT_CATALOGUE)
  );

  if (missingUpstream.length > 0) {
    console.warn(
      '[entitlements] These keys are gated on the client but the credit ' +
        'service does not catalogue them, so the gates never block: ' +
        missingUpstream.join(', ')
    );
  }
  if (missingLocally.length > 0) {
    console.warn(
      '[entitlements] The credit service catalogues these keys and nothing ' +
        'on the client reads them: ' +
        missingLocally.join(', ')
    );
  }
}
